// One boundary per region (docs/ARCHITECTURE.md §2.6): a thrown error in one part unmounts that part and shows a
// line in its place; the rest keeps drawing. The root one ("app") is the worst case: a reload button, not a blank page.
import { Component, type ErrorInfo, type ReactNode } from "react";
import { useFeed } from "../state/feed";
import { reportError } from "./report";
import styles from "./ErrorBoundary.module.css";

interface Props {
  region: string;
  children: ReactNode;
  /** When this changes, a broken region tries again (e.g. new events arrived). */
  resetKey?: unknown;
}
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`Nowish: the ${this.props.region} broke`, error, info.componentStack);
    reportError("part", error, `Nowish: the ${this.props.region} broke`);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    if (this.props.region === "app") {
      return (
        <div className={styles.page} role="alert">
          Something broke.
          <button type="button" className={styles.reload} onClick={() => location.reload()}>Reload</button>
        </div>
      );
    }
    return <div className={styles.region} role="alert">The {this.props.region} isn't working.</div>;
  }
}

/** A region's boundary that tries again whenever new events arrive (most of what a region draws comes from them). */
export function Region({ name, children }: { name: string; children: ReactNode }) {
  const events = useFeed((s) => s.events);
  return <ErrorBoundary region={name} resetKey={events}>{children}</ErrorBoundary>;
}
